import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LibraryWorkspace } from '../utils/workspaceLibrary';
import type { WorkspaceFolder } from '../utils/workspaceFolders';
import type { FindShared } from '../utils/pickedReferences';
import { projectScope, readSharedSources, type ProjectScope, type ReadReferenceNodes, type SharedSource } from '../utils/projectReferences';
import { onWorkspaceSaved } from '../utils/workspaceSaves';

interface Options {
    openWorkspaceId: string | null;
    workspaces: LibraryWorkspace[];
    folders: WorkspaceFolder[];
    read: ReadReferenceNodes;
}

export interface ProjectReferences {
    scope: ProjectScope | null;
    sources: SharedSource[];
    /** Workspaces read so far. */
    readIds: ReadonlySet<string>;
    loading: boolean;
    /** Reads the other workspaces again; unchanged ones come from memory. */
    refresh: () => void;
    findShared: FindShared;
}

/**
 * The Reference nodes of the other workspaces in the open workspace's project: read in the
 * background when the workspace opens, again whenever the reference list opens, and again when
 * this session saves one of them (a workspace just left is saved a moment after the next opens).
 */
export const useProjectReferences = ({ openWorkspaceId, workspaces, folders, read }: Options): ProjectReferences => {
    // Saves made here since the library list was read: it does not know of them, and a workspace
    // read before such a save would otherwise be taken from memory as unchanged
    const [savedHere, setSavedHere] = useState<Readonly<Record<string, number>>>({});
    useEffect(() => onWorkspaceSaved((id, at) => setSavedHere(prev => ((prev[id] ?? 0) >= at ? prev : { ...prev, [id]: at }))), []);
    const known = useMemo(
        () => workspaces.map(w => ((savedHere[w.id] ?? 0) > w.lastSaved ? { ...w, lastSaved: savedHere[w.id] } : w)),
        [workspaces, savedHere],
    );
    const scope = useMemo(() => projectScope(openWorkspaceId, known, folders), [openWorkspaceId, known, folders]);
    // Changes when the project, its workspaces or their last saves change
    const scopeKey = scope ? `${scope.projectId}|${scope.siblings.map(w => `${w.id}:${w.lastSaved}`).join(',')}` : '';
    const [sources, setSources] = useState<SharedSource[]>([]);
    const [readIds, setReadIds] = useState<ReadonlySet<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const latest = useRef({ scope, read, scopeKey });
    latest.current = { scope, read, scopeKey };
    const run = useRef(0);

    const refresh = useCallback(() => {
        const { scope: current, read: reader, scopeKey: key } = latest.current;
        const token = ++run.current;
        if (!current || current.siblings.length === 0) {
            setSources([]);
            setReadIds(new Set());
            setLoading(false);
            return;
        }
        setLoading(true);
        void readSharedSources(current.siblings, reader).then((got) => {
            // Only the latest read, for the project still open, is kept
            if (token !== run.current || latest.current.scopeKey !== key) return;
            setSources(got);
            setReadIds(new Set(got.map(source => source.workspaceId)));
            setLoading(false);
        });
    }, []);

    // Another project: what was read for the last one no longer applies
    const projectId = scope?.projectId ?? null;
    useEffect(() => {
        setSources([]);
        setReadIds(new Set());
    }, [projectId]);
    useEffect(() => { refresh(); }, [scopeKey, refresh]);

    const findShared = useCallback<FindShared>((workspaceId, nodeId) => {
        const source = sources.find(s => s.workspaceId === workspaceId);
        if (!source) return undefined;
        return source.nodes.find(node => node.id === nodeId) ?? null;
    }, [sources]);

    return { scope, sources, readIds, loading, refresh, findShared };
};
